import { useState, useEffect, useRef } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Button,
  ButtonGroup,
} from "@chakra-ui/react"
import { DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const YEAR_COLORS = [
  { bar: "rgba(156, 163, 175, 0.7)", border: "rgba(156, 163, 175, 1)" },   // year-2: gray
  { bar: "rgba(96, 165, 250, 0.7)", border: "rgba(96, 165, 250, 1)" },     // year-1: blue
  { bar: "rgba(52, 211, 153, 0.7)", border: "rgba(52, 211, 153, 1)" },     // current: green
]

const GrowthComparison = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [rawData, setRawData] = useState([])
  const [metric, setMetric] = useState("amount") // "amount" or "units"
  const [downloading, setDownloading] = useState(false)
  const chartRef = useRef(null)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")
  const posColor = useColorModeValue("green.600", "green.300")
  const negColor = useColorModeValue("red.600", "red.300")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/insights/growth-comparison`,
        {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        },
        { signal }
      )
      if (res.data.status === 1) {
        setRawData(res.data.data || [])
      } else {
        setRawData([])
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("GrowthComparison fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  // Pivot raw data into { years: [2024, 2025, 2026], months: { 1: { 2024: {units, amount}, ... }, ... } }
  const pivoted = (() => {
    if (!rawData.length) return null
    const years = [...new Set(rawData.map((r) => r.year))].sort()
    const months = {}
    for (const row of rawData) {
      if (!months[row.month_num]) months[row.month_num] = {}
      months[row.month_num][row.year] = {
        units: parseInt(row.total_units) || 0,
        amount: parseFloat(row.total_amount) || 0,
      }
    }
    return { years, months }
  })()

  const calcGrowth = (curr, prev) => {
    if (!prev || prev === 0) return null
    return ((curr - prev) / prev) * 100
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  const formatNum = (val) => (parseInt(val) || 0).toLocaleString()

  const formatGrowth = (val) => {
    if (val === null || val === undefined) return "-"
    const prefix = val > 0 ? "+" : ""
    return `${prefix}${val.toFixed(1)}%`
  }

  // Build chart data
  const chartData = (() => {
    if (!pivoted) return null
    const { years, months } = pivoted
    const labels = MONTH_NAMES
    const datasets = years.map((year, idx) => ({
      label: String(year),
      data: MONTH_NAMES.map((_, mi) => {
        const m = months[mi + 1]
        if (!m || !m[year]) return 0
        return metric === "amount" ? m[year].amount : m[year].units
      }),
      backgroundColor: YEAR_COLORS[idx]?.bar || "rgba(100,100,100,0.5)",
      borderColor: YEAR_COLORS[idx]?.border || "rgba(100,100,100,1)",
      borderWidth: 1,
    }))
    return { labels, datasets }
  })()

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      datalabels: { display: false },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y
            return metric === "amount"
              ? `${ctx.dataset.label}: R ${val.toLocaleString()}`
              : `${ctx.dataset.label}: ${val.toLocaleString()} units`
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val) =>
            metric === "amount"
              ? `R ${(val / 1000).toFixed(0)}k`
              : val.toLocaleString(),
        },
      },
    },
  }

  // Build table rows
  const tableRows = (() => {
    if (!pivoted) return []
    const { years, months } = pivoted
    return MONTH_NAMES.map((name, mi) => {
      const monthNum = mi + 1
      const m = months[monthNum] || {}
      const vals = years.map((y) => m[y] || { units: 0, amount: 0 })
      const growths = vals.map((v, i) => {
        if (i === 0) return null
        const prev = vals[i - 1]
        return metric === "amount"
          ? calcGrowth(v.amount, prev.amount)
          : calcGrowth(v.units, prev.units)
      })
      return { name, monthNum, vals, growths }
    })
  })()

  // Totals row
  const totals = (() => {
    if (!pivoted) return null
    const { years, months } = pivoted
    const sums = years.map((y) => {
      let units = 0, amount = 0
      for (const mn of Object.keys(months)) {
        if (months[mn][y]) {
          units += months[mn][y].units
          amount += months[mn][y].amount
        }
      }
      return { units, amount }
    })
    const growths = sums.map((s, i) => {
      if (i === 0) return null
      const prev = sums[i - 1]
      return metric === "amount"
        ? calcGrowth(s.amount, prev.amount)
        : calcGrowth(s.units, prev.units)
    })
    return { sums, growths }
  })()

  const handleDownload = async () => {
    setDownloading(true)
    try {
      if (!pivoted) return
      const { years } = pivoted
      const workbook = new ExcelJS.Workbook()
      const ws = workbook.addWorksheet("3-Year Growth")

      // Header row
      const headers = ["Month"]
      for (let i = 0; i < years.length; i++) {
        headers.push(`${years[i]} Sales`, `${years[i]} Units`)
        if (i > 0) headers.push(`${years[i - 1]}→${years[i]} Growth %`)
      }
      ws.addRow(headers)
      ws.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
      })

      // Data rows
      for (const row of tableRows) {
        const cells = [row.name]
        for (let i = 0; i < row.vals.length; i++) {
          cells.push(row.vals[i].amount, row.vals[i].units)
          if (i > 0) {
            const g = calcGrowth(row.vals[i].amount, row.vals[i - 1].amount)
            cells.push(g !== null ? `${g.toFixed(1)}%` : "-")
          }
        }
        ws.addRow(cells)
      }

      // Totals row
      if (totals) {
        const cells = ["TOTAL"]
        for (let i = 0; i < totals.sums.length; i++) {
          cells.push(totals.sums[i].amount, totals.sums[i].units)
          if (i > 0) {
            const g = calcGrowth(totals.sums[i].amount, totals.sums[i - 1].amount)
            cells.push(g !== null ? `${g.toFixed(1)}%` : "-")
          }
        }
        const totalRow = ws.addRow(cells)
        totalRow.font = { bold: true }
      }

      // Column widths
      ws.columns.forEach((col) => { col.width = 16 })
      ws.getColumn(1).width = 10

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `Growth_3Year_${supplier}_${retailer}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const hasData = rawData.length > 0

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>
            Metric
          </Text>
          <ButtonGroup size="sm" isAttached>
            <Button
              colorScheme={metric === "amount" ? "teal" : "gray"}
              variant={metric === "amount" ? "solid" : "outline"}
              onClick={() => setMetric("amount")}
            >
              Sales (Rands)
            </Button>
            <Button
              colorScheme={metric === "units" ? "teal" : "gray"}
              variant={metric === "units" ? "solid" : "outline"}
              onClick={() => setMetric("units")}
            >
              Units
            </Button>
          </ButtonGroup>
        </Box>
        {hasData && (
          <Button
            size="sm"
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            onClick={handleDownload}
            isLoading={downloading}
            loadingText="Downloading"
          >
            Download Report
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : !hasData ? (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">No sales data found</Text>
        </Box>
      ) : (
        <>
          {/* Chart */}
          {chartData && (
            <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} p={4} mb={4}>
              <Heading size="sm" mb={3}>
                {metric === "amount" ? "Monthly Sales (Rands)" : "Monthly Sales (Units)"} &mdash; 3-Year Comparison
              </Heading>
              <Box h="350px">
                <Bar ref={chartRef} data={chartData} options={chartOptions} />
              </Box>
            </Box>
          )}

          {/* Table */}
          {pivoted && (
            <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
              <Box p={3} borderBottom="1px" borderColor={borderColor}>
                <Flex justify="space-between" align="center">
                  <Heading size="sm">Monthly Breakdown</Heading>
                  <Badge colorScheme="purple">{pivoted.years.join(" / ")}</Badge>
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {metric === "amount" ? "Sales value" : "Units sold"} per month with year-over-year growth percentages
                </Text>
              </Box>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th>Month</Th>
                      {pivoted.years.map((y, i) => (
                        <Th key={y} isNumeric>
                          {y}
                        </Th>
                      ))}
                      {pivoted.years.length >= 2 && (
                        <Th isNumeric>
                          {pivoted.years[pivoted.years.length - 2]}→{pivoted.years[pivoted.years.length - 1]}
                        </Th>
                      )}
                      {pivoted.years.length >= 3 && (
                        <Th isNumeric>
                          {pivoted.years[0]}→{pivoted.years[1]}
                        </Th>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tableRows.map((row) => {
                      const display = (v) =>
                        metric === "amount" ? formatCurrency(v.amount) : formatNum(v.units)
                      return (
                        <Tr key={row.monthNum} _hover={{ bg: hoverBg }}>
                          <Td fontWeight="medium" fontSize="xs">
                            {row.name}
                          </Td>
                          {row.vals.map((v, i) => (
                            <Td key={i} isNumeric fontSize="xs">
                              {display(v)}
                            </Td>
                          ))}
                          {/* Latest growth (year-1 -> current) */}
                          {pivoted.years.length >= 2 && (
                            <Td
                              isNumeric
                              fontSize="xs"
                              fontWeight="bold"
                              color={
                                row.growths[row.growths.length - 1] === null
                                  ? "gray.400"
                                  : row.growths[row.growths.length - 1] >= 0
                                  ? posColor
                                  : negColor
                              }
                            >
                              {formatGrowth(row.growths[row.growths.length - 1])}
                            </Td>
                          )}
                          {/* Older growth (year-2 -> year-1) */}
                          {pivoted.years.length >= 3 && (
                            <Td
                              isNumeric
                              fontSize="xs"
                              color={
                                row.growths[1] === null
                                  ? "gray.400"
                                  : row.growths[1] >= 0
                                  ? posColor
                                  : negColor
                              }
                            >
                              {formatGrowth(row.growths[1])}
                            </Td>
                          )}
                        </Tr>
                      )
                    })}
                    {/* Totals row */}
                    {totals && (
                      <Tr fontWeight="bold" bg={headerBg}>
                        <Td fontSize="xs">TOTAL</Td>
                        {totals.sums.map((s, i) => (
                          <Td key={i} isNumeric fontSize="xs">
                            {metric === "amount" ? formatCurrency(s.amount) : formatNum(s.units)}
                          </Td>
                        ))}
                        {pivoted.years.length >= 2 && (
                          <Td
                            isNumeric
                            fontSize="xs"
                            fontWeight="bold"
                            color={
                              totals.growths[totals.growths.length - 1] === null
                                ? "gray.400"
                                : totals.growths[totals.growths.length - 1] >= 0
                                ? posColor
                                : negColor
                            }
                          >
                            {formatGrowth(totals.growths[totals.growths.length - 1])}
                          </Td>
                        )}
                        {pivoted.years.length >= 3 && (
                          <Td
                            isNumeric
                            fontSize="xs"
                            color={
                              totals.growths[1] === null
                                ? "gray.400"
                                : totals.growths[1] >= 0
                                ? posColor
                                : negColor
                            }
                          >
                            {formatGrowth(totals.growths[1])}
                          </Td>
                        )}
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default GrowthComparison
